/**
 * useLyricEntry
 *
 * Gesangstext-Eingabesitzung im MuseScore-Stil: Note wählen, tippen, `Leertaste` rückt zur nächsten
 * Note, `-` trennt die Silbe im Wort, `_` setzt ein Melisma, `Umschalt+Leertaste` geht zurück.
 *
 * Zwei bewusste Festlegungen (nicht rückbauen):
 * 1. **Die Sitzung schreibt in den lokalen `musicXml`-Ref, nicht nach oben.** Ein Schreibvorgang pro
 *    Silbe über den Store würde den `file`-Prop der Vorschau ändern; deren `watchDebounced` ruft
 *    `clearSelection()` – die Auswahl und damit die Sitzung wäre nach jeder Silbe zerstört. Nach
 *    oben gemeldet wird **einmal** am Sitzungsende (`onSessionCommitted`).
 * 2. **Der Silbentyp steht nie im Sitzungsspeicher**, er wird bei jedem Commit aus dem XML
 *    abgeleitet (`setVerseLyricInMusicXml` → `syllabicFor`). Dadurch bleibt die Sitzung nach Undo,
 *    Herumklicken und Re-Render korrekt.
 *
 * Sub-Composable von {@link @/composables/useOsmdInteractiveScore}.
 */
import {type Ref, ref, watch} from 'vue';

import {
    EDITABLE_VERSE_NUMBERS,
    type NoteInfo,
    type VerseLyric,
    type VerseLyricInput,
    noteKey,
    readVerseLyric,
    setVerseLyricInMusicXml,
} from '@/composables/musicXmlDom';

export type VerseNumber = typeof EDITABLE_VERSE_NUMBERS[number];

/**
 * Wie der Entwurf abgeschlossen wurde:
 * `space` Wortende + weiter · `hyphen` Silbentrennung + weiter · `melisma` gehaltener Ton + weiter ·
 * `back` eine Note zurück · `stay` sichern, ohne zu bewegen (Enter/Esc/Klick auf eine andere Note).
 */
export type LyricCommitMode = 'space' | 'hyphen' | 'melisma' | 'back' | 'stay';

/** Endlos-Schutz für die Skip-Schleife (Pausen/fremde Stimmen), großzügig über jedem Realfall. */
const MAX_SKIP = 64;

export interface LyricEntryDeps {
    musicXml: Ref<string | null>;
    /** Primäre Auswahl – Ziel des Entwurfs und Anker des schwebenden Eingabefelds. */
    selectedNote: Ref<NoteInfo | null>;
    selectNextNote: () => void;
    selectPreviousNote: () => void;
    /** Wird am Sitzungsende gerufen, **wenn** Text geschrieben wurde (Übernahme nach oben). */
    onSessionCommitted?: (musicXml: string) => void;
    notify?: (message: string) => void;
}

export interface LyricEntry {
    activeVerse: Ref<VerseNumber>;
    isLyricActive: Ref<boolean>;
    lyricDraft: Ref<string>;
    startLyricEntry: () => void;
    commitLyric: (mode: LyricCommitMode) => void;
    endLyricEntry: () => void;
}

/**
 * Rohen Entwurf in die Schreibdaten übersetzen. Ein **End**-Minus wirkt wie die Bindestrich-Taste,
 * ein **führendes** Minus wird verworfen: über `begin`/`end` entscheidet die Position im Wort, nicht
 * die Tipperei (bewusste Abweichung von music21s `setTextAndSyllabic('-lo')`).
 * Ohne Abschlusszeichen (`stay`/`back`) bleibt die **vorhandene** Silbentrennung erhalten – nur so
 * lässt sich ein Tippfehler mitten in `Him-mels-zelt` korrigieren, ohne das Wort zu zerreißen.
 */
export function parseLyricDraft(
    raw: string, mode: LyricCommitMode, existing: VerseLyric | null,
): VerseLyricInput {
    let text = raw.trim();
    while (text.startsWith('-')) text = text.slice(1).trim();
    let typedHyphen = false;
    while (text.endsWith('-')) {
        typedHyphen = true;
        text = text.slice(0, -1).trim();
    }
    const inWord = existing?.syllabic === 'begin' || existing?.syllabic === 'middle';
    if (mode === 'melisma') return {text, continuesWord: false, extend: true};
    const extend = existing?.extend ?? false;
    if (mode === 'hyphen') return {text, continuesWord: true, extend};
    if (mode === 'space') return {text, continuesWord: typedHyphen, extend};
    return {text, continuesWord: typedHyphen || inWord, extend};
}

export function useLyricEntry(deps: LyricEntryDeps): LyricEntry {
    const {musicXml, selectedNote, selectNextNote, selectPreviousNote, onSessionCommitted, notify} = deps;

    const activeVerse = ref<VerseNumber>('4');
    const isLyricActive = ref(false);
    const lyricDraft = ref('');

    /** Stimme, in der die Sitzung begann – hält den Cursor aus fremden Stimmen heraus
     *  (z. B. den unsichtbaren Bass-Trägern der Stimme 2 in der Griffschrift-Ausgabe). */
    let sessionVoice: number | null = null;
    let sessionDirty = false;
    /** Unterdrückt das Sichern des Entwurfs, während die Sitzung selbst weiterrückt. */
    let internalMove = false;

    function prefill(): void {
        const info = selectedNote.value;
        const xml = musicXml.value;
        lyricDraft.value = info && xml ? readVerseLyric(xml, info, activeVerse.value)?.text ?? '' : '';
    }

    /** Schreibt den aktuellen Entwurf auf `info`. Leerer Entwurf entfernt eine vorhandene Silbe;
     *  ist dort ohnehin keine, passiert nichts (reine Navigation erzeugt so kein History-Rauschen). */
    function commitTo(info: NoteInfo, mode: LyricCommitMode): boolean {
        const xml = musicXml.value;
        if (!xml) return false;
        const existing = readVerseLyric(xml, info, activeVerse.value);
        const parsed = parseLyricDraft(lyricDraft.value, mode, existing);
        if (!parsed.text && !existing) return true;
        const updated = setVerseLyricInMusicXml(xml, info, parsed.text ? parsed : null, activeVerse.value);
        if (!updated) {
            notify?.('Gesangstext konnte nicht gesetzt werden.');
            return false;
        }
        musicXml.value = updated;
        sessionDirty = true;
        return true;
    }

    /** Nächste/vorherige **textfähige** Note: Pausen und fremde Stimmen werden hier übersprungen,
     *  die restlichen Töne **einer Zählzeit** dagegen von der Navigation selbst (der Orchestrator
     *  reicht `selectNextNote`/`selectPreviousNote` mit `skipSamePosition` herein) – so trägt ein
     *  Akkord genau eine Silbe. Abbruch, sobald sich die Auswahl nicht mehr ändert (`selectNote`
     *  clampt am Stückrand). */
    function advance(direction: 1 | -1): void {
        internalMove = true;
        try {
            for (let i = 0; i < MAX_SKIP; i++) {
                const before = selectedNote.value ? noteKey(selectedNote.value) : null;
                if (direction > 0) selectNextNote();
                else selectPreviousNote();
                const current = selectedNote.value;
                const after = current ? noteKey(current) : null;
                if (after === before) return;
                if (current && !current.isRest && (sessionVoice === null || current.voice === sessionVoice)) return;
            }
        } finally {
            internalMove = false;
            prefill();
        }
    }

    function startLyricEntry(): void {
        const info = selectedNote.value;
        if (!info || info.isRest || !musicXml.value) {
            notify?.('Gesangstext braucht eine klingende Note.');
            return;
        }
        sessionVoice = info.voice;
        sessionDirty = false;
        isLyricActive.value = true;
        prefill();
    }

    function commitLyric(mode: LyricCommitMode): void {
        const info = selectedNote.value;
        if (!info) return;
        if (!commitTo(info, mode)) return;
        if (mode === 'back') advance(-1);
        else if (mode !== 'stay') advance(1);
    }

    function endLyricEntry(): void {
        if (!isLyricActive.value) return;
        if (selectedNote.value) commitTo(selectedNote.value, 'stay');
        isLyricActive.value = false;
        lyricDraft.value = '';
        sessionVoice = null;
        const dirty = sessionDirty;
        sessionDirty = false;
        if (dirty && musicXml.value) onSessionCommitted?.(musicXml.value);
    }

    // Klick auf eine andere Note mitten in der Sitzung: den schwebenden Entwurf auf der ZUVOR
    // gewählten Note sichern, dann aus der neuen vorbelegen. `flush: 'sync'`, damit der
    // `internalMove`-Guard des eigenen Weiterrückens greift (Vue-Watches sind sonst asynchron).
    watch(selectedNote, (_current, previous) => {
        if (!isLyricActive.value) return;
        if (!internalMove && previous) commitTo(previous, 'stay');
        if (!internalMove) prefill();
    }, {flush: 'sync'});

    // Strophenwechsel belegt sofort aus der neuen Strophe vor – `flush: 'sync'`, damit das
    // Eingabefeld nicht einen Tick lang den Text der alten Strophe zeigt.
    watch(activeVerse, prefill, {flush: 'sync'});

    return {activeVerse, isLyricActive, lyricDraft, startLyricEntry, commitLyric, endLyricEntry};
}
